import { useState, useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { 
  ArrowLeft, 
  Upload, 
  Loader2, 
  CalendarIcon,
  Image as ImageIcon,
  X
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es, enUS } from 'date-fns/locale';
import { cn } from '../lib/utils';
import Sidebar from './Sidebar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NewsForm = ({ news, onSuccess, onCancel }) => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const isEditing = !!news;

  const [formData, setFormData] = useState({
    badge_es: news?.badge?.es || '',
    badge_en: news?.badge?.en || '',
    title_es: news?.title?.es || '',
    title_en: news?.title?.en || '',
    subtitle_es: news?.subtitle?.es || '',
    subtitle_en: news?.subtitle?.en || '',
    frequency_es: news?.frequency?.es || '',
    frequency_en: news?.frequency?.en || '',
    description_es: news?.description?.es || '',
    description_en: news?.description?.en || '',
    body_es: news?.body?.es || '',
    body_en: news?.body?.en || '',
    backgroundImageUrl: news?.backgroundImageUrl || '',
    portraitImageUrl: news?.portraitImageUrl || '',
    publishedAt: news?.publishedAt ? new Date(news.publishedAt) : new Date()
  });

  const [uploading, setUploading] = useState({ background: false, portrait: false });
  const [saving, setSaving] = useState(false);
  const backgroundInputRef = useRef(null);
  const portraitInputRef = useRef(null);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleImageUpload = async (file, type) => {
    if (!file) return;

    // Validate file size
    if (file.size > 2 * 1024 * 1024) {
      toast.error(t('fileTooLarge'));
      return;
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error(t('invalidFileType'));
      return;
    }

    setUploading(prev => ({ ...prev, [type]: true }));

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(`${API}/upload`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const imageUrl = response.data.url;
      handleInputChange(type === 'background' ? 'backgroundImageUrl' : 'portraitImageUrl', imageUrl);
      toast.success(t('imageUploaded'));
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(t('errorOccurred'));
    } finally {
      setUploading(prev => ({ ...prev, [type]: false }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.backgroundImageUrl) {
      toast.error(language === 'es' ? 'La imagen de fondo es obligatoria' : 'Background image is required');
      return;
    }

    setSaving(true);

    try {
      const payload = {
        ...formData,
        publishedAt: formData.publishedAt.toISOString(),
        createdBy: user?.name || user?.userId || 'unknown'
      };

      if (isEditing) {
        await axios.put(`${API}/news/${news.id}`, payload);
        toast.success(t('newsUpdated'));
      } else {
        await axios.post(`${API}/news`, payload);
        toast.success(t('newsCreated'));
      }

      onSuccess();
    } catch (error) {
      console.error('Save error:', error);
      toast.error(t('errorOccurred'));
    } finally {
      setSaving(false);
    }
  };

  const getImageSrc = (url) => {
    if (!url) return null;
    if (url.startsWith('/api')) {
      return `${process.env.REACT_APP_BACKEND_URL}${url}`;
    }
    return url;
  };

  const ImageUploadZone = ({ type, currentUrl, inputRef, required }) => (
    <div className="space-y-2">
      <Label className="text-[#1E3A5F] font-medium">
        {type === 'background' ? t('backgroundImage') : t('portraitImage')}
        {required && <span className="text-red-500 ml-1">*</span>}
      </Label>
      
      {currentUrl ? (
        <div className="relative group">
          <div className={cn(
            "rounded-lg overflow-hidden border border-slate-200",
            type === 'background' ? 'aspect-video' : 'w-32 h-32'
          )}>
            <img 
              src={getImageSrc(currentUrl)} 
              alt={type}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.src = type === 'background'
                  ? 'https://images.unsplash.com/photo-1767868280782-fc108d087050?crop=entropy&cs=srgb&fm=jpg&q=85'
                  : 'https://randomuser.me/api/portraits/lego/1.jpg';
              }}
            />
          </div>
          <Button
            type="button"
            variant="destructive"
            size="sm"
            className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={() => handleInputChange(type === 'background' ? 'backgroundImageUrl' : 'portraitImageUrl', '')}
          >
            <X className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={() => inputRef.current?.click()}
            disabled={uploading[type]}
          >
            {uploading[type] ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <Upload className="h-4 w-4 mr-1" />
                {language === 'es' ? 'Cambiar' : 'Change'}
              </>
            )}
          </Button>
        </div>
      ) : (
        <div
          onClick={() => inputRef.current?.click()}
          className={cn(
            "border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer transition-all",
            "hover:bg-slate-50 hover:border-[#1E3A5F]",
            required && !currentUrl ? 'border-red-400 bg-red-50/30' : 'border-slate-300',
            type === 'background' ? 'p-12 aspect-video' : 'p-8 w-32 h-32'
          )}
        >
          {uploading[type] ? (
            <Loader2 className="h-8 w-8 animate-spin text-[#1E3A5F]" />
          ) : (
            <>
              <ImageIcon className="h-8 w-8 text-slate-400 mb-2" />
              <p className="text-sm text-slate-500 text-center">{t('dragDropImage')}</p>
              <p className="text-xs text-slate-400 mt-1">{t('maxFileSize')}</p>
            </>
          )}
        </div>
      )}
      
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        className="hidden"
        onChange={(e) => handleImageUpload(e.target.files?.[0], type)}
        data-testid={`${type}-image-input`}
      />
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar extraNav={
        <Button
          variant="ghost"
          onClick={onCancel}
          className="w-full justify-start text-white/80 hover:text-white hover:bg-white/10 gap-2 mt-2"
          data-testid="back-button"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('back')}
        </Button>
      } />

      {/* Main Content */}
      <main className="lg:ml-64 pt-14 lg:pt-0 p-4 lg:p-8">
        <div className="max-w-6xl mx-auto">
          <form onSubmit={handleSubmit}>
            {/* Header */}
            <div className="flex items-center justify-between mb-6 lg:mb-8">
              <h1 className="font-barlow text-2xl lg:text-3xl font-bold uppercase tracking-tight text-[#1E3A5F]">
                {isEditing ? t('editNews') : t('addNews')}
              </h1>
              <div className="flex items-center gap-2 lg:gap-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onCancel}
                  disabled={saving}
                  data-testid="cancel-form-button"
                >
                  {t('cancel')}
                </Button>
                <Button
                  type="submit"
                  disabled={saving}
                  className="bg-[#8B1538] hover:bg-[#6D102B] text-white font-medium uppercase tracking-wide"
                  data-testid="save-news-button"
                >
                  {saving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      <span className="hidden sm:inline">{t('saving')}</span>
                    </>
                  ) : (
                    t('save')
                  )}
                </Button>
              </div>
            </div>

            {/* Images Section */}
            <Card className="border-slate-200 mb-6">
              <CardHeader className="border-b border-slate-100 bg-slate-50/50">
                <CardTitle className="font-barlow text-lg font-semibold uppercase tracking-tight text-[#1E3A5F]">
                  {language === 'es' ? 'Imágenes' : 'Images'}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <ImageUploadZone type="background" currentUrl={formData.backgroundImageUrl} inputRef={backgroundInputRef} required />
                  <ImageUploadZone type="portrait" currentUrl={formData.portraitImageUrl} inputRef={portraitInputRef} />
                </div>
              </CardContent>
            </Card>

            {/* Date Section */}
            <Card className="border-slate-200 mb-6">
              <CardHeader className="border-b border-slate-100 bg-slate-50/50">
                <CardTitle className="font-barlow text-lg font-semibold uppercase tracking-tight text-[#1E3A5F]">
                  {t('publishedAt')}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      className={cn(
                        "w-full max-w-sm justify-start text-left font-normal",
                        !formData.publishedAt && "text-muted-foreground"
                      )}
                      data-testid="date-picker-trigger"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.publishedAt ? (
                        format(formData.publishedAt, "PPP", { locale: language === 'es' ? es : enUS })
                      ) : (
                        <span>{t('publishedAt')}</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.publishedAt}
                      onSelect={(date) => handleInputChange('publishedAt', date || new Date())}
                      initialFocus
                      locale={language === 'es' ? es : enUS}
                    />
                  </PopoverContent>
                </Popover>
              </CardContent>
            </Card>

            {/* Bilingual Content Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Spanish Content */}
              <Card className="border-slate-200">
                <CardHeader className="border-b border-slate-100 bg-[#8B1538]/5">
                  <CardTitle className="font-barlow text-lg font-semibold uppercase tracking-tight text-[#8B1538] flex items-center gap-2">
                    <span className="inline-flex items-center rounded-md bg-[#8B1538] px-2 py-1 text-xs font-medium text-white">
                      ES
                    </span>
                    {t('spanishContent')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  <div className="space-y-2">
                    <Label className="text-[#1E3A5F] font-medium">{t('badge')}</Label>
                    <Input
                      value={formData.badge_es}
                      onChange={(e) => handleInputChange('badge_es', e.target.value)}
                      placeholder="Ej: Flota carguera B737-400F"
                      required
                      className="border-slate-200 focus-visible:ring-[#1E3A5F]"
                      data-testid="badge-es-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#1E3A5F] font-medium">{t('title')}</Label>
                    <Input
                      value={formData.title_es}
                      onChange={(e) => handleInputChange('title_es', e.target.value)}
                      placeholder="Ej: Atención y servicio comercial"
                      required
                      className="border-slate-200 focus-visible:ring-[#1E3A5F]"
                      data-testid="title-es-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#1E3A5F] font-medium">{t('subtitle')}</Label>
                    <Input
                      value={formData.subtitle_es}
                      onChange={(e) => handleInputChange('subtitle_es', e.target.value)}
                      placeholder="Ej: San José (SJO) | Panamá (PTY)"
                      required
                      className="border-slate-200 focus-visible:ring-[#1E3A5F]"
                      data-testid="subtitle-es-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#1E3A5F] font-medium">{t('frequency')}</Label>
                    <Input
                      value={formData.frequency_es}
                      onChange={(e) => handleInputChange('frequency_es', e.target.value)}
                      placeholder="Ej: Martes y viernes"
                      required
                      className="border-slate-200 focus-visible:ring-[#1E3A5F]"
                      data-testid="frequency-es-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#1E3A5F] font-medium">{t('description')}</Label>
                    <Textarea
                      value={formData.description_es}
                      onChange={(e) => handleInputChange('description_es', e.target.value)}
                      placeholder="Descripción en español..."
                      required
                      rows={6}
                      className="border-slate-200 focus-visible:ring-[#1E3A5F] resize-none"
                      data-testid="description-es-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#1E3A5F] font-medium">{language === 'es' ? 'Cuerpo' : 'Body'}</Label>
                    <Textarea
                      value={formData.body_es}
                      onChange={(e) => handleInputChange('body_es', e.target.value)}
                      placeholder="Cuerpo del artículo en español..."
                      rows={8}
                      className="border-slate-200 focus-visible:ring-[#1E3A5F] resize-none"
                      data-testid="body-es-input"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* English Content */}
              <Card className="border-slate-200">
                <CardHeader className="border-b border-slate-100 bg-[#1E3A5F]/5">
                  <CardTitle className="font-barlow text-lg font-semibold uppercase tracking-tight text-[#1E3A5F] flex items-center gap-2">
                    <span className="inline-flex items-center rounded-md bg-[#1E3A5F] px-2 py-1 text-xs font-medium text-white">
                      EN
                    </span>
                    {t('englishContent')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  <div className="space-y-2">
                    <Label className="text-[#1E3A5F] font-medium">{t('badge')}</Label>
                    <Input
                      value={formData.badge_en}
                      onChange={(e) => handleInputChange('badge_en', e.target.value)}
                      placeholder="E.g.: B737-400F freighter fleet"
                      required
                      className="border-slate-200 focus-visible:ring-[#1E3A5F]"
                      data-testid="badge-en-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#1E3A5F] font-medium">{t('title')}</Label>
                    <Input
                      value={formData.title_en}
                      onChange={(e) => handleInputChange('title_en', e.target.value)}
                      placeholder="E.g.: Commercial service and support"
                      required
                      className="border-slate-200 focus-visible:ring-[#1E3A5F]"
                      data-testid="title-en-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#1E3A5F] font-medium">{t('subtitle')}</Label>
                    <Input
                      value={formData.subtitle_en}
                      onChange={(e) => handleInputChange('subtitle_en', e.target.value)}
                      placeholder="E.g.: San Jose (SJO) | Panama (PTY)"
                      required
                      className="border-slate-200 focus-visible:ring-[#1E3A5F]"
                      data-testid="subtitle-en-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#1E3A5F] font-medium">{t('frequency')}</Label>
                    <Input
                      value={formData.frequency_en}
                      onChange={(e) => handleInputChange('frequency_en', e.target.value)}
                      placeholder="E.g.: Tuesdays and Fridays"
                      required
                      className="border-slate-200 focus-visible:ring-[#1E3A5F]"
                      data-testid="frequency-en-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#1E3A5F] font-medium">{t('description')}</Label>
                    <Textarea
                      value={formData.description_en}
                      onChange={(e) => handleInputChange('description_en', e.target.value)}
                      placeholder="Description in English..."
                      required
                      rows={6}
                      className="border-slate-200 focus-visible:ring-[#1E3A5F] resize-none"
                      data-testid="description-en-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#1E3A5F] font-medium">{language === 'es' ? 'Cuerpo' : 'Body'}</Label>
                    <Textarea
                      value={formData.body_en}
                      onChange={(e) => handleInputChange('body_en', e.target.value)}
                      placeholder="Article body in English..."
                      rows={8}
                      className="border-slate-200 focus-visible:ring-[#1E3A5F] resize-none"
                      data-testid="body-en-input"
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default NewsForm;
